import {
    Duration,
    eachDayOfInterval,
    eachHourOfInterval,
    eachMonthOfInterval,
    eachWeekOfInterval,
    sub as subDuration,
    startOfDay,
    endOfDay,
    startOfWeek,
    endOfWeek,
    startOfMonth,
    endOfMonth,
    isThisYear,
} from 'date-fns';
import { Interval, ViewMode } from 'app/agriculture/AgTimeline/types';
import { BucketSize } from 'app/agriculture/queries/snapshots/config/bucketConfig';
import { ChartInterval } from 'app/agriculture/hooks/useAggregateChartData/useAggregateChartData';

const weekStartsOn = 1;

export interface ViewModeConfig {
    /** The label shown in the select field */
    label: string;
    /** How much time is represented by the visible part of the timeline */
    intervalDuration: Duration;
    /** How much time is shifted when the bumper is clicked */
    bumpDuration: Duration;
    /** Whether the timeline pellets should be condensed */
    condensePellets: boolean;
    /** A method to define a new visible interval based on cursorTime */
    getNewVisibleInterval: (cursorTime: Date) => Interval<Date>;
    /** A method to split the interval into smaller axis sections */
    getAxisSections: (interval: Interval<Date | number>) => Date[];
    /** How much time is represented by an axis section */
    axisSectionDuration: Duration;
    /** The desired format for the axis labels */
    axisLabelTimestampFormat: string;
    /** Visual treatment of the axis sections, to control if it's represented with borders or not */
    axisLabelType: 'point' | 'section';
    /** The desired format for the cursor time label */
    cursorTimeFormat: (cursorTime: Date) => string;
    aggregationConfig: {
        bucketSize: BucketSize;
        intervalUnit: ChartInterval;
    };
}

export const viewModeConfig: {
    [key in keyof typeof ViewMode]: ViewModeConfig;
} = {
    Day: {
        label: 'Day',
        condensePellets: false,
        intervalDuration: {
            days: 1,
        },
        bumpDuration: {
            hours: 2,
        },
        getNewVisibleInterval: (cursorTime) => ({
            start: startOfDay(cursorTime),
            end: endOfDay(cursorTime),
        }),
        getAxisSections: eachHourOfInterval,
        axisSectionDuration: { hours: 1 },
        axisLabelTimestampFormat: 'haaa',
        axisLabelType: 'point',
        cursorTimeFormat: (cursorTime: Date) =>
            isThisYear(cursorTime) ? 'dd MMM h:mmaaa' : 'dd MMM yyyy - h:mmaaa',
        aggregationConfig: {
            bucketSize: 'day',
            intervalUnit: 'hour',
        },
    },
    Week: {
        label: 'Week',
        condensePellets: false,
        intervalDuration: {
            weeks: 1,
        },
        bumpDuration: {
            days: 1,
        },
        getNewVisibleInterval: (cursorTime) => ({
            start: startOfWeek(cursorTime, { weekStartsOn }),
            end: endOfWeek(cursorTime, { weekStartsOn }),
        }),
        getAxisSections: eachDayOfInterval,
        axisSectionDuration: { days: 1 },
        axisLabelTimestampFormat: 'EEEE d',
        axisLabelType: 'section',
        cursorTimeFormat: (cursorTime: Date) =>
            isThisYear(cursorTime) ? 'dd MMM h:mmaaa' : 'dd MMM yyyy - h:mmaaa ',
        aggregationConfig: {
            bucketSize: 'week',
            intervalUnit: 'day',
        },
    },
    Month: {
        label: 'Month',
        condensePellets: true,
        intervalDuration: {
            months: 1,
        },
        bumpDuration: {
            weeks: 1,
        },
        getNewVisibleInterval: (cursorTime) => ({
            start: startOfWeek(startOfMonth(cursorTime), { weekStartsOn }),
            end: endOfWeek(endOfMonth(cursorTime), { weekStartsOn }),
        }),
        getAxisSections: (interval) => eachWeekOfInterval(interval, { weekStartsOn }),
        axisSectionDuration: { weeks: 1 },
        axisLabelTimestampFormat: 'd MMMM',
        axisLabelType: 'section',
        cursorTimeFormat: () => 'd MMMM yyyy',
        aggregationConfig: {
            bucketSize: 'month',
            intervalUnit: 'day',
        },
    },
    SixMonth: {
        label: '6 Months',
        condensePellets: true,
        intervalDuration: {
            months: 6,
        },
        bumpDuration: {
            months: 1,
        },
        getNewVisibleInterval: (cursorTime) => ({
            start: startOfMonth(subDuration(cursorTime, { months: 5 })),
            end: endOfMonth(cursorTime),
        }),
        getAxisSections: eachMonthOfInterval,
        axisSectionDuration: { months: 1 },
        axisLabelTimestampFormat: 'MMMM',
        axisLabelType: 'section',
        cursorTimeFormat: () => 'd MMMM yyyy',
        aggregationConfig: {
            bucketSize: 'year',
            intervalUnit: 'month',
        },
    },
};
